// ═══════════════════════════════════════════════════════════════════════
// Temps › Réglages › Jours fériés — calendrier daté (une ligne par férié et par année).
// La RH propose (ou prépare l'année suivante à partir des fériés fixes), une autre personne valide.
// Un férié mobile est saisi « prévisionnel », puis confirmé ou déplacé à l'annonce officielle.
// Rien n'est supprimé : un férié s'annule avec un motif.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { CalendarDays, Plus, CheckCircle2, Ban, MoveRight, BadgeCheck, CopyPlus, ChevronLeft, ChevronRight } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette, formaterDate } from '../../design/composants';
import { lire, ecrire, messageErreur, useReglages, useChargement, NavReglages, Fenetre, Champ, PiedFenetre, BadgeStatut, nomPersonne } from './reglagesCommun';

const NATURES = { CHOME_PAYE: 'Chômé et payé', CHOME: 'Chômé', CONVENTIONNEL: 'Selon la convention' };
const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const jourDeSemaine = (iso) => JOURS[new Date(`${iso}T12:00:00Z`).getUTCDay()];

function FenetreFerie({ sites, annee, onFermer, onTermine }) {
  const [f, setF] = useState({ date: `${annee}-01-01`, libelle: '', site_id: '', type_ferie: 'FIXE', etat: 'CONFIRME', nature: 'CHOME_PAYE', source: '' });
  const [erreur, setErreur] = useState('');
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const envoyer = async () => {
    try { await ecrire('post', '/feries', { ...f, site_id: f.site_id || null }); onTermine('Jour férié proposé. Il s\'appliquera après validation par une autre personne.'); } catch (e) { setErreur(messageErreur(e)); }
  };
  return (
    <Fenetre titre="Proposer un jour férié" Icone={Plus} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <Champ libelle="Date" requis><input type="date" className="rh-champ-saisie" value={f.date} onChange={maj('date')} /></Champ>
      <Champ libelle="Libellé" requis><input className="rh-champ-saisie" value={f.libelle} onChange={maj('libelle')} placeholder="Ex. Fête de l'Indépendance, Tabaski" /></Champ>
      <Champ libelle="Type" aide="Fixe : même date chaque année (recopié en préparant l'année suivante). Mobile : date annoncée chaque année.">
        <select className="rh-champ-saisie" value={f.type_ferie} onChange={(e) => setF({ ...f, type_ferie: e.target.value, etat: e.target.value === 'MOBILE' ? 'PREVISIONNEL' : 'CONFIRME' })}>
          <option value="FIXE">Fixe</option><option value="MOBILE">Mobile (religieux)</option>
        </select>
      </Champ>
      {f.type_ferie === 'MOBILE' && (
        <Champ libelle="Date">
          <select className="rh-champ-saisie" value={f.etat} onChange={maj('etat')}>
            <option value="PREVISIONNEL">Prévisionnelle (à confirmer à l'annonce)</option><option value="CONFIRME">Confirmée</option>
          </select>
        </Champ>
      )}
      <Champ libelle="Portée">
        <select className="rh-champ-saisie" value={f.site_id} onChange={maj('site_id')}>
          <option value="">Nationale (tous les sites)</option>
          {sites.map((s) => <option key={s.id} value={s.id}>Site {s.libelle}</option>)}
        </select>
      </Champ>
      <Champ libelle="Nature">
        <select className="rh-champ-saisie" value={f.nature} onChange={maj('nature')}>
          {Object.entries(NATURES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Champ>
      <Champ libelle="Source"><input className="rh-champ-saisie" value={f.source} onChange={maj('source')} placeholder="Ex. loi 74-52, communiqué du ministère" /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={Plus} onClick={envoyer} disabled={!f.date || f.libelle.trim().length < 2}>Proposer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

function FenetreDeplacer({ ferie, onFermer, onTermine }) {
  const [f, setF] = useState({ nouvelle_date: ferie.date, motif: '', source: '' });
  const [erreur, setErreur] = useState('');
  const envoyer = async () => {
    try {
      await ecrire('patch', `/feries/${ferie.id}/deplacer`, f);
      onTermine(ferie.validation === 'VALIDE'
        ? 'Déplacement proposé. L\'ancienne date reste fériée jusqu\'à sa validation.'
        : 'Date modifiée. La proposition reste à valider.');
    } catch (e) { setErreur(messageErreur(e)); }
  };
  return (
    <Fenetre titre={`Déplacer « ${ferie.libelle} »`} Icone={MoveRight} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <Champ libelle="Nouvelle date" requis><input type="date" className="rh-champ-saisie" value={f.nouvelle_date} onChange={(e) => setF({ ...f, nouvelle_date: e.target.value })} /></Champ>
      <Champ libelle="Source"><input className="rh-champ-saisie" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="Ex. communiqué officiel du…" /></Champ>
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="primaire" Icone={MoveRight} onClick={envoyer} disabled={f.motif.trim().length < 3 || f.nouvelle_date === ferie.date}>Déplacer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

function FenetreAnnuler({ ferie, onFermer, onTermine }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  return (
    <Fenetre titre={`Annuler « ${ferie.libelle} »`} Icone={Ban} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--sg-texte-2)' }}>Le férié reste dans l'historique avec le motif. {ferie.validation === 'VALIDE' ? 'Les journées de cette date seront recalculées.' : ''}</p>
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="danger" Icone={Ban} disabled={motif.trim().length < 3}
          onClick={async () => { try { await ecrire('patch', `/feries/${ferie.id}/annuler`, { motif }); onTermine('Jour férié annulé.'); } catch (e) { setErreur(messageErreur(e)); } }}>Annuler le férié</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export default function ReglagesFeriesPage() {
  const { droits, options, erreur: erreurDroits } = useReglages();
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [message, setMessage] = useState('');
  const [erreurAction, setErreurAction] = useState('');
  const [fenetre, setFenetre] = useState(null);
  const { donnees: liste, erreur, recharger } = useChargement(() => lire('/feries', { annee }), [annee]);

  const terminer = (m) => { setFenetre(null); setMessage(m); setErreurAction(''); recharger(); };
  const agir = async (fn, m) => { try { await fn(); terminer(m); } catch (e) { setErreurAction(messageErreur(e)); } };
  const preparer = () => agir(async () => {
    const r = await ecrire('post', '/feries/preparer-annee', { annee_source: annee, annee_cible: annee + 1 });
    setAnnee(annee + 1);
    return r;
  }, `Année ${annee + 1} préparée à partir des fériés fixes validés de ${annee} : à valider.`);
  const aValider = (liste || []).filter((f) => f.validation === 'A_VALIDER' && f.etat !== 'ANNULE').length;

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'Réglages' }, { libelle: 'Jours fériés' }]} titre="Jours fériés"
          sousTitre="Un férié ne compte dans le calcul du temps qu'après validation par une autre personne que son auteur."
          actions={droits?.proposer && (
            <>
              <Bouton Icone={CopyPlus} onClick={preparer} title={`Recopier les fériés fixes validés de ${annee} sur ${annee + 1}`}>Préparer {annee + 1}</Bouton>
              <Bouton variante="primaire" Icone={Plus} onClick={() => setFenetre({ type: 'proposer' })}>Proposer un férié</Bouton>
            </>
          )} />
        <NavReglages actif="feries" />
        {(erreurDroits || erreur || erreurAction) && <BandeauInfo ton="danger">{erreurDroits || erreur || erreurAction}</BandeauInfo>}
        {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
        {aValider > 0 && <BandeauInfo ton="alerte" titre={`${aValider} jour${aValider > 1 ? 's' : ''} férié${aValider > 1 ? 's' : ''} à valider`}>Tant qu'ils ne sont pas validés, ces jours sont calculés comme des jours ordinaires.</BandeauInfo>}

        <Carte titre={`Année ${annee}`} Icone={CalendarDays} action={(
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <Bouton petit variante="discret" Icone={ChevronLeft} onClick={() => setAnnee(annee - 1)}>{annee - 1}</Bouton>
            <Bouton petit variante="discret" onClick={() => setAnnee(annee + 1)}>{annee + 1}<ChevronRight size={14} aria-hidden="true" /></Bouton>
          </span>
        )}>
          {!liste ? <Squelette largeur="60%" /> : liste.length === 0 ? (
            <EtatVide Icone={CalendarDays} titre={`Aucun jour férié en ${annee}`} texte={droits?.proposer ? `Proposez les fériés, ou préparez l'année depuis ${annee - 1}.` : undefined} />
          ) : (
            <div className="rh-conteneur-tableau">
              <table className="rh-tableau">
                <thead><tr><th>Date</th><th>Férié</th><th>Nature</th><th>Statut</th><th>Proposé par</th><th aria-label="Actions" /></tr></thead>
                <tbody>
                  {liste.map((f) => (
                    <tr key={f.id} style={{ opacity: f.etat === 'ANNULE' ? 0.55 : 1 }}>
                      <td style={{ whiteSpace: 'nowrap' }}><span className="rh-cellule-principale">{formaterDate(f.date, true)}</span><span className="rh-cellule-secondaire" style={{ display: 'block' }}>{jourDeSemaine(f.date)}</span></td>
                      <td>
                        <span className="rh-cellule-principale">{f.libelle}</span>
                        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>
                          {f.site_nom ? `Site ${f.site_nom}` : 'National'} · {f.type_ferie === 'MOBILE' ? 'Mobile' : 'Fixe'}
                          {f.remplace_date ? ` · remplace le ${formaterDate(f.remplace_date)}` : ''}
                        </span>
                      </td>
                      <td>{NATURES[f.nature] || f.nature}</td>
                      <td>
                        {f.etat === 'ANNULE' ? <BadgeStatut statut="ANNULE" /> : <BadgeStatut statut={f.validation} />}
                        {f.etat === 'PREVISIONNEL' && <Badge ton="info">Date prévisionnelle</Badge>}
                        {f.etat === 'ANNULE' && f.motif_annulation && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{f.motif_annulation}</span>}
                      </td>
                      <td>
                        {nomPersonne(f.auteur_prenom, f.auteur_nom)}
                        {f.valideur_nom && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Validé par {nomPersonne(f.valideur_prenom, f.valideur_nom)}</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {f.etat !== 'ANNULE' && f.validation === 'A_VALIDER' && droits?.valider && (
                          <Bouton petit Icone={CheckCircle2} disabled={f.created_by === droits.moi}
                            title={f.created_by === droits.moi ? 'Vous avez proposé ce férié : une autre personne doit le valider.' : undefined}
                            onClick={() => agir(() => ecrire('patch', `/feries/${f.id}/valider`), 'Jour férié validé : les journées de cette date seront recalculées.')}>Valider</Bouton>
                        )}
                        {f.etat === 'PREVISIONNEL' && droits?.proposer && (
                          <Bouton petit variante="discret" Icone={BadgeCheck} onClick={() => agir(() => ecrire('patch', `/feries/${f.id}/confirmer`, {}), 'Date confirmée.')}>Confirmer</Bouton>
                        )}
                        {f.etat !== 'ANNULE' && droits?.proposer && (
                          <Bouton petit variante="discret" Icone={MoveRight} onClick={() => setFenetre({ type: 'deplacer', ferie: f })}>Déplacer</Bouton>
                        )}
                        {f.etat !== 'ANNULE' && (f.validation === 'VALIDE' ? droits?.valider : droits?.proposer) && (
                          <Bouton petit variante="discret" Icone={Ban} onClick={() => setFenetre({ type: 'annuler', ferie: f })}>Annuler</Bouton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>

        {fenetre?.type === 'proposer' && <FenetreFerie sites={options.sites} annee={annee} onFermer={() => setFenetre(null)} onTermine={terminer} />}
        {fenetre?.type === 'deplacer' && <FenetreDeplacer ferie={fenetre.ferie} onFermer={() => setFenetre(null)} onTermine={terminer} />}
        {fenetre?.type === 'annuler' && <FenetreAnnuler ferie={fenetre.ferie} onFermer={() => setFenetre(null)} onTermine={terminer} />}
      </div>
    </Layout>
  );
}
